import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { sealQueue, type PendingSeal } from './sealQueue';
import { lettersApi } from '../letters/api';

interface OfflineState {
  /** Sealed while offline, not yet confirmed by the server. */
  pending: PendingSeal[];
  flushing: boolean;
  lastError: string | null;
}

const initialState: OfflineState = { pending: [], flushing: false, lastError: null };

export const loadPendingSeals = createAsyncThunk('offline/load', () => sealQueue.all());

export const enqueueSeal = createAsyncThunk(
  'offline/enqueue',
  async (seal: PendingSeal) => {
    await sealQueue.put(seal);
    // Ask the service worker to wake us when connectivity returns. Browsers
    // without Background Sync fall back to the `online` event listener.
    const sync = (
      navigator.serviceWorker?.ready as unknown as Promise<{
        sync?: { register: (tag: string) => Promise<void> };
      }>
    );
    void sync.then((reg) => reg.sync?.register('sync-letters'));
    return seal;
  },
);

/** Drop a queued seal the user cancelled or that is now meaningless. */
export const dropPendingSeal = createAsyncThunk('offline/drop', async (id: string) => {
  await sealQueue.remove(id);
  return id;
});

/** Sign-out: queued letters cannot be sent without the session, so forget them. */
export const clearSealQueue = createAsyncThunk('offline/clear', async () => {
  await sealQueue.clear();
});

/**
 * Try to deliver every queued letter. Runs on reconnect, on the service
 * worker's background-sync signal, and after every successful online seal.
 *
 * Retry policy: a 2xx removes the entry. A 4xx will never succeed (bad
 * relationship, signed out, not a member) so it is dropped rather than retried
 * forever. Anything else — network, 5xx — stays queued for the next flush.
 */
export const flushPendingSeals = createAsyncThunk(
  'offline/flush',
  async (_: void, { dispatch }) => {
    const pending = await sealQueue.all();
    if (pending.length === 0) return { flushed: 0, remaining: pending };

    let flushed = 0;
    for (const seal of pending) {
      try {
        const res = await fetch('/api/letters', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            relationshipId: seal.relationshipId,
            body: seal.body,
            postage: seal.postage,
            stampDesignId: seal.stampDesignId,
            stationeryId: seal.stationeryId,
            envelopeId: seal.envelopeId,
            composeMs: seal.composeMs,
          }),
          credentials: 'include',
        });
        // A 2xx is delivered. A 4xx will never succeed (bad relationship,
        // signed out, not a member) — drop rather than retry forever. Network
        // errors and 5xx stay queued for the next flush.
        if (res.ok || (res.status >= 400 && res.status < 500)) {
          await sealQueue.remove(seal.id);
          if (res.ok) flushed++;
        }
      } catch {
        // Still offline; keep it queued.
      }
    }

    if (flushed > 0) {
      // The server state changed even though this ran outside RTK Query.
      dispatch(lettersApi.util.invalidateTags(['Letter']));
    }
    return { flushed, remaining: await sealQueue.all() };
  },
);

const offlineSlice = createSlice({
  name: 'offline',
  initialState,
  reducers: {
    clearError(state) {
      state.lastError = null;
    },
  },
  extraReducers: (b) => {
    b.addCase(loadPendingSeals.fulfilled, (state, action) => {
      state.pending = action.payload;
    });
    b.addCase(enqueueSeal.fulfilled, (state, action) => {
      state.pending.push(action.payload);
      state.lastError = null;
    });
    b.addCase(dropPendingSeal.fulfilled, (state, action) => {
      state.pending = state.pending.filter((p) => p.id !== action.payload);
    });
    b.addCase(clearSealQueue.fulfilled, (state) => {
      state.pending = [];
      state.lastError = null;
    });
    b.addCase(flushPendingSeals.pending, (state) => {
      state.flushing = true;
    });
    b.addCase(flushPendingSeals.fulfilled, (state, action) => {
      state.flushing = false;
      // IndexedDB is authoritative; the thunk removed what it delivered.
      state.pending = action.payload.remaining;
    });
    b.addCase(flushPendingSeals.rejected, (state, action) => {
      state.flushing = false;
      state.lastError = action.error.message ?? 'could not flush pending letters';
    });
  },
});

export const { clearError } = offlineSlice.actions;
export default offlineSlice.reducer;